import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import Switch from "react-switch";
import { Button, Card, CardBody, Col, Input, Row, Table } from 'reactstrap';
import { ItemDropdownMenu, ServiceThreadSpinner, SortHeader, SpinnerButton } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, helpers } from '../utils';

const ACTIONS = {
   GetOpenAndRecentCounts: 1,
   SaveCount: 2,
   GetVarianceAndExceptionData: 3,
   GetVarianceReport: 4,
   CompleteCount: 5,
   ClosedFG: 6
};

const VARIANCE_REPORT_TYPES = {
   summary: 0,
   fullDetail: 1,
   manualDetail: 2
};

export default function InventoryCountPosting(props) {
   const [countData, setCountData] = useState([]);
   const [countSort, setCountSort] = useState({ field: "countId", direction: "desc" });
   const [downloadPdf, setDownloadPdf] = useState(true);
   const [invExSort, setInvExSort] = useState({ field: "itemNumber", direction: "desc" });
   const [screenOptions, setScreenOptions] = useState({ varRptType: VARIANCE_REPORT_TYPES.summary });
   const [selectedCount, setSelectedCount] = useState(null);
   const [workingAction, setWorkingAction] = useState(null);
   const [varianceData, setVarianceData] = useState({});

   useEffect(refreshData, []);

   function getVarianceReport() {
      setWorkingAction(ACTIONS.GetVarianceReport);
      api
         .postDownloadBinary('Report/InventoryCount', { reportType: screenOptions.varRptType, selectedCount: selectedCount }, constants.MIME_TYPES.pdf, "Variance_Report.pdf", "There was an error getting the Variance Report")
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorkingAction(null));
   }

   function handleCaseExceptionChange(field, value, index) {
      const copy = _.slice(varianceData.case);
      copy[index][field] = value;
      setVarianceData({ ...varianceData, case: copy });
   }

   function handleCloseFinishedGoods() {
      setWorkingAction(ACTIONS.ClosedFG)
      api
         .post("Inventory/SaveCount", selectedCount)
         .then(r => {
            if (r.data.success) {
               toast.success("Inventory Count Posting Complete");
               refreshData();
            } else {
               toast.error("Error completing Count Posting");
               console.error(r.data.message);
               setWorkingAction(null);
            }
         })
         .catch(err => {
            api.catchHandler(err);
            setWorkingAction(null);
         })
   }

   function handleCompleteCount() {
      setWorkingAction(ACTIONS.CompleteCount)
      const inventoryPayload = {};
      const trackingPayload = [];
      _.forEach(varianceData.inventory, invEx => {
         if (invEx.update) {
            inventoryPayload[invEx.itemNumber] = invEx.varianceQuantity;
            trackingPayload.push({
               notes: invEx.notes,
               inventoryCountId: selectedCount.countId,
               itemNumber: invEx.itemNumber,
               varianceQuantity: invEx.varianceAmount,
               unitCost: invEx.currentCost
            });
         }
      });
      const casePayload = [];
      _.forEach(varianceData.case, caseEx => {
         if (caseEx.update) {
            casePayload.push({
               caseId: caseEx.caseId,
               currentSite: selectedCount.inventorySite,
               netWeight: caseEx.netWeight
            })
         }
      });
      const payload = {
         selectedCount: selectedCount,
         varianceItems: inventoryPayload,
         varianceTracking: trackingPayload,
         transferTracking: casePayload
      };
      api
         .post("Inventory/CompleteCount", payload)
         .then(r => {
            if (r.data.success) {
               toast.success("Inventory Count Posting Complete!")
               setSelectedCount(null);
               setVarianceData({})
               refreshData();
            } else {
               toast.error("Error completing count");
               console.error(r.data.message);
               setWorkingAction(null);
            }
         })
         .catch(err => {
            api.catchHandler(err);
            setWorkingAction(null);
         })
   }

   function handleInventoryExceptionChange(field, value, index) {
      const copy = _.slice(varianceData.inventory);
      copy[index][field] = value;
      setVarianceData({ ...varianceData, inventory: copy });
   }

   function handlePrintInvetoryVarianceReport(param) {
      if (downloadPdf) {
         printInventoryVarianceReport(param);
      }
      else {
         printInventoryVarianceReportXLS(param);
      }
   }

   function loadVariance(countDatum) {
      setWorkingAction(ACTIONS.GetVarianceAndExceptionData);
      setSelectedCount(countDatum);
      api
         .post("Inventory/GetVarianceAndExceptionData", countDatum)
         .then(r => {
            if (r.data.success) {
               setVarianceData(r.data.message);
            } else {
               toast.error("Error loading Variance Data");
               console.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorkingAction(null));
   }

   function onCountSortChange(field, direction) {
      if (field !== countSort.field) {
         setCountSort({ ...countSort, field: field })
      } else {
         setCountSort({ ...countSort, direction: direction })
      }
   }

   function onInvExSortChange(field, direction) {
      if (field !== countSort.field) {
         setInvExSort({ ...invExSort, field: field })
      } else {
         setInvExSort({ ...invExSort, direction: direction })
      }
   }

   function printInventoryVarianceReport(inventory) {
      api
         .postDownloadBinary(
            "Report/InventoryVarianceReport",
            {
               countId: inventory.countId
            },
            constants.MIME_TYPES.pdf,
            "Inventory_Variance_Report.pdf",
            "There was an error getting the Inventory Variance Report"
         )
         .then((response) => {
            if (response?.data === false) {
               toast.error(response.message);
            }
         })
         .catch(api.catchHandler);
   }

   function printInventoryVarianceReportXLS(inventory) {
      api
         .postGetBlob(
            "Report/InventoryVarianceReportXLS",
            {
               countId: inventory.countId
            }
         )
         .then((response) => {
            if (response && response.data) {
               const XLSX_FILENAME = `Inventory_Variance_Report.xlsx`;
               helpers.downloadExport(
                  response.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(response.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function refreshData() {
      setWorkingAction(ACTIONS.GetOpenAndRecentCounts);
      api
         .fetch("Inventory/GetOpenAndRecentCounts")
         .then(r => {
            if (r.data.success) {
               setCountData(r.data.message);
            } else {
               toast.error("Error fetching Open and Recent Counts");
               console.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorkingAction(null));
   }

   function toggleCountLock(countDatum) {
      setWorkingAction(ACTIONS.SaveCount);
      countDatum.locked = !countDatum.locked;
      api
         .post("Inventory/SaveCount", countDatum)
         .then(r => {
            if (r.data.success) {
               toast.success(`Count ${countDatum.locked ? "locked" : "unlocked"}!`)
               refreshData();
            } else {
               toast.error("Error locking Count");
               console.error(r.data.message);
               setWorkingAction(null);
            }
         })
         .catch(err => {
            api.catchHandler(err);
            setWorkingAction(null);
         });
   }

   return (
      document.title = `Inventory Count Posting`,
      <DesktopContainer screenName="Inventory Count Posting">
         {/* List of Inventory Counts */}
         <Card className="dashboard-card">
            <CardBody>
               <Row className="mt-0 mb-3">
                  <Col className="text-center">
                     <span className="h5">Inventory Counts</span>
                  </Col>
                  <Col className="text-center" md={2}>
                     <Row className="d-flex align-items-center justify-content-center">
                        <Col xs="5" className="d-flex align-items-center">
                           <div className="filter-definition-caption mx-1 p-0">
                              {downloadPdf ? "Download PDF" : "Download XLSX"}
                           </div>
                           <Switch
                              onChange={setDownloadPdf}
                              checked={downloadPdf || false}
                              className="mx-1"
                              checkedIcon={false}
                              uncheckedIcon={false}
                           />
                        </Col>
                     </Row>
                  </Col>
               </Row>
               <Table size="sm" className="data-table my-1">
                  <thead className="table-header">
                     <tr>
                        <th width="20%">
                           <SortHeader
                              displayName="Id"
                              field="countId"
                              sortDirection={countSort.direction}
                              sorted={countSort.field === "countId"}
                              callback={onCountSortChange}
                           />
                        </th>
                        <th width="20%">
                           <SortHeader
                              displayName="Location"
                              field="inventorySite"
                              sortDirection={countSort.direction}
                              sorted={countSort.field === "inventorySite"}
                              callback={onCountSortChange}
                           />
                        </th>
                        <th width="20%">
                           <SortHeader
                              displayName="Count Date"
                              field="countDate"
                              sortDirection={countSort.direction}
                              sorted={countSort.field === "countDate"}
                              callback={onCountSortChange}
                           />
                        </th>
                        <th width="15%">
                           <SortHeader
                              displayName="Locked"
                              field="locked"
                              sortDirection={countSort.direction}
                              sorted={countSort.field === "locked"}
                              callback={onCountSortChange}
                           />
                        </th>
                        <th width="15%">
                           <SortHeader
                              displayName="Post"
                              field="countId"
                              sortDirection={countSort.direction}
                              sorted={countSort.field === "countId"}
                              callback={onCountSortChange}
                           />
                        </th>
                        <th width="10%" />
                     </tr>
                  </thead>
                  <tbody>
                     {workingAction === ACTIONS.GetOpenAndRecentCounts
                        ? <tr><td colSpan={6}><ServiceThreadSpinner on={true} /></td></tr>
                        : countData.length === 0
                           ? <tr><td colSpan={6} className="h5 fw-bold text-center">No Data Found</td></tr>
                           : _.map(countData, (datum, i) => {
                              return (
                                 <tr key={i} className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}>
                                    <td>{datum.displayId}</td>
                                    <td>{datum.inventorySite}</td>
                                    <td>{dateHelpers.monthDayAndYear(datum.countDate)}</td>
                                    <td>
                                       {datum.completed
                                          ? "Locked"
                                          : datum.locked
                                             ? <span className="shredLink" onClick={() => toggleCountLock(datum)}>Unlock</span>
                                             : <span className="shredLink" onClick={() => toggleCountLock(datum)}>Lock Count</span>
                                       }
                                    </td>
                                    <td>
                                       {datum.completed
                                          ? "Complete"
                                          : datum.locked
                                             ? <span className="shredLink" onClick={() => loadVariance(datum)}>Load Variance</span>
                                             : "Lock count before posting"
                                       }
                                    </td>
                                    <td><span className={classNames("shredLink", { "d-none": !datum.completed })} onClick={() => handlePrintInvetoryVarianceReport(datum)}>Print Report</span></td>
                                 </tr>
                              )
                           })
                     }
                  </tbody>
                  <tfoot className="table-footer">
                     <tr>
                        <td colSpan={6} />
                     </tr>
                  </tfoot>
               </Table>
            </CardBody>
         </Card>

         <ServiceThreadSpinner on={workingAction === ACTIONS.GetVarianceAndExceptionData}>
            {/* Inventory Variances can print the variance reports */}
            <Card className={classNames("dashboard-card", { "d-none": _.isNil(varianceData.inventory) })}>
               <CardBody>
                  <Row className="mt-0 mb-3">
                     <Col className="text-center">
                        <span className="h5">Inventory Item Exceptions</span>
                     </Col>
                  </Row>
                  <Table size="sm" className="data-table my-1">
                     <thead className="table-header">
                        <tr>
                           <th width="20%">
                              <SortHeader
                                 displayName="Item"
                                 field="itemNumber"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "itemNumber"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="8%" className="text-end">
                              <SortHeader
                                 displayName={<>Counted<br />Quantity</>}
                                 field="countedQty"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "countedQty"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="8%" className="text-end">
                              <SortHeader
                                 displayName={<>GP<br />Quantity</>}
                                 field="qtyOnHand"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "qtyOnHand"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="8%" className="text-end">
                              <SortHeader
                                 displayName={<>Variance<br />Quantity</>}
                                 field="varianceQuantity"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "varianceQuantity"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="8%" className="text-end">
                              <SortHeader
                                 displayName="Cost"
                                 field="currentCost"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "currentCost"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="8%" className="text-end">
                              <SortHeader
                                 displayName={<>Variance<br />Amount</>}
                                 field="varianceAmount"
                                 sortDirection={invExSort.direction}
                                 sorted={invExSort.field === "varianceAmount"}
                                 callback={onInvExSortChange}
                              />
                           </th>
                           <th width="15%" className="ps-3">
                              Variance<br />Quantity
                           </th>
                           <th width="20%">
                              Note
                           </th>
                           <th width="5%">
                              Update
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(varianceData.inventory, (invEx, i) => {
                           const zeroVariance = invEx.varianceAmount === 0;
                           invEx.update = !zeroVariance;
                           return (
                              <tr key={`${i}-${invEx.itemNumber}`} className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}>
                                 <ItemDropdownMenu
                                    itemNumber={invEx.itemNumber}
                                    itemDescription={invEx.itemWithDescription}
                                 />
                                 <td className="text-end align-middle">{helpers.formatWithDecimalPlaces(invEx.countedQty)}</td>
                                 <td className="text-end align-middle">{helpers.formatWithDecimalPlaces(invEx.qtyOnHand)}</td>
                                 <td className="text-end align-middle">{helpers.formatWithDecimalPlaces(invEx.varianceQuantity)}</td>
                                 <td className="text-end align-middle">{helpers.formatCurrency(invEx.currentCost)}</td>
                                 <td className="text-end align-middle">{helpers.formatCurrency(invEx.varianceAmount)}</td>
                                 <td>
                                    <NumberFormat
                                       className={classNames("form-control", { "readonly-input": zeroVariance })}
                                       value={invEx.varianceQuantity}
                                       onValueChange={({ floatValue }) => {
                                          if (floatValue > 99999999) floatValue = 99999999;
                                          if (floatValue < -99999999) floatValue = -99999999;
                                          handleInventoryExceptionChange("varianceAmount", floatValue, i);
                                       }}
                                       decimalScale={5}
                                       readOnly={zeroVariance}
                                    />
                                 </td>
                                 <td>
                                    <Input
                                       value={invEx.notes || ""}
                                       onChange={e => handleInventoryExceptionChange("notes", e.target.value, i)}
                                    />
                                 </td>
                                 <td className="text-center align-middle">
                                    <Input
                                       className={classNames({ "d-none": zeroVariance })}
                                       type="checkbox"
                                       checked={invEx.update}
                                       onChange={e => handleInventoryExceptionChange("update", e.target.checked, i)}
                                    />
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={9} />
                        </tr>
                     </tfoot>
                  </Table>
                  <Row className="mt-3">
                     <Col md={6} lg={3} xl={2}>
                        <Button className="shredButton" onClick={getVarianceReport}>
                           Print Variance Report
                        </Button>
                     </Col>
                     <Col className="pt-1">
                        <Input
                           type="radio"
                           name="varRptType"
                           checked={screenOptions.varRptType === VARIANCE_REPORT_TYPES.summary}
                           onChange={() => setScreenOptions({ ...screenOptions, varRptType: VARIANCE_REPORT_TYPES.summary })}
                           className="me-1"
                        />
                        <span>Summary Report</span>
                        <Input
                           type="radio"
                           name="varRptType"
                           checked={screenOptions.varRptType === VARIANCE_REPORT_TYPES.fullDetail}
                           onChange={() => setScreenOptions({ ...screenOptions, varRptType: VARIANCE_REPORT_TYPES.fullDetail })}
                           className="ms-4 me-1"
                        />
                        <span>Full Detail Report</span>
                        <Input
                           type="radio"
                           name="varRptType"
                           checked={screenOptions.varRptType === VARIANCE_REPORT_TYPES.manualDetail}
                           onChange={() => setScreenOptions({ ...screenOptions, varRptType: VARIANCE_REPORT_TYPES.manualDetail })}
                           className="ms-4 me-1"
                        />
                        <span>Manual Count Detail Report</span>
                     </Col>
                  </Row>
               </CardBody>
            </Card>

            {/* Case Variances - can call handleCompleteCount */}
            <Card className={classNames("dashboard-card", { "d-none": _.isNil(varianceData.case) })}>
               <CardBody>
                  <Row className="mt-0 mb-3">
                     <Col className="text-center">
                        <span className="h5">Case Exceptions</span>
                     </Col>
                  </Row>
                  <Table size="sm" className="data-table my-1">
                     <thead className="table-header">
                        <tr>
                           <th width="15%">Case Number</th>
                           <th width="15%">Lot Number</th>
                           <th width="15%">Item</th>
                           <th width="10%">Scanned Weight</th>
                           <th width="10%">Expected Weight</th>
                           <th width="15%" className="text-center">Update Case Weight</th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(varianceData.case, (caseEx, i) => {
                           caseEx.update = true;
                           return (
                              <tr key={caseEx.caseId} className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}>
                                 <td>{caseEx.caseNumber}</td>
                                 <td>{caseEx.lotNumber}</td>
                                 <ItemDropdownMenu
                                    itemNumber={caseEx.itemNumber}
                                    itemDescription={caseEx.itemWithDesc}
                                 />
                                 <td>{helpers.formatWithDecimalPlaces(caseEx.netWeight)}</td>
                                 <td>{helpers.formatWithDecimalPlaces(caseEx.expectedWeight)}</td>
                                 <td className="text-center align-middle">
                                    <Input
                                       type="checkbox"
                                       checked={caseEx.update}
                                       onChange={e => handleCaseExceptionChange("update", e.target.checked, i)}
                                    />
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={6} />
                        </tr>
                     </tfoot>
                  </Table>
                  <Row>
                     <Col>
                        <SpinnerButton
                           className="shredButton"
                           onClick={handleCompleteCount}
                           on={workingAction === ACTIONS.CompleteCount}
                           disabled={!_.isNull(workingAction)}
                        >
                           Create Variance and Complete Count
                        </SpinnerButton>
                     </Col>
                  </Row>
               </CardBody>
            </Card>

            {/* Unscanned Items or Scanned but Not In GP Items - can call handleClosedFinishedGoods */}
            <Card className={classNames("dashboard-card", { "d-none": _.isNil(varianceData.unscanned) })}>
               <CardBody>
                  <Row className="mt-0 mb-3">
                     <Col className="text-center">
                        <span className="h5">Inventory Item Exceptions</span>
                     </Col>
                  </Row>

                  <Row>
                     <Col>
                        <span>Unscanned Cases (GP thinks we have these cases)</span>
                     </Col>
                  </Row>
                  {varianceData.unscanned && varianceData.unscanned.length === 0
                     ? <Row><Col><span className="fw-bold text-danger">No unscanned cases found</span></Col></Row>
                     : <Table>
                        <thead className="table-header">
                           <tr>
                              <th>Item</th>
                              <th>Quantity</th>
                              <th>Lot Number</th>
                              <th>Cost</th>
                              <th>Status</th>
                              <th>Bin Number</th>
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(varianceData.unscanned, (vdu, i) => {
                              return (
                                 <tr key={`${i}-${vdu.itemNumber}`} className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}>
                                    <td>{vdu.itemWithDesc}</td>
                                    <td>{vdu.expectedQuantity}</td>
                                    <td>{vdu.lotNumber}</td>
                                    <td>{helpers.formatCurrency(vdu.currentCost)}</td>
                                    <td>{vdu.caseStatus}</td>
                                    <td>{vdu.binNumber}</td>
                                 </tr>
                              )
                           })}
                        </tbody>
                        <tfoot className="table-footer">
                           <tr>
                              <td colSpan={6} />
                           </tr>
                        </tfoot>
                     </Table>
                  }

                  <Row className="mt-3">
                     <Col>
                        <span>Scanned Cases Not in Inventory (GP does not know about these cases)</span>
                     </Col>
                  </Row>
                  {varianceData.scanned && varianceData.scanned.length === 0
                     ? <Row><Col><span className="fw-bold text-danger text-center">No scanned missing cases found</span></Col></Row>
                     : <Table>
                        <thead className="table-header">
                           <tr>
                              <th>Item</th>
                              <th>Counted Quantity</th>
                              <th>Net Weight</th>
                              <th>Lot Number</th>
                              <th>Cost</th>
                              <th>Status</th>
                              <th>Bin Number</th>
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(varianceData.scanned, (vdu, i) => {
                              return (
                                 <tr key={vdu.itemNumber} className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}>
                                    <td>{vdu.itemWithDesc}</td>
                                    <td>{vdu.expectedQuantity}</td>
                                    <td>{vdu.netWieght}</td>
                                    <td>{vdu.lotNumber}</td>
                                    <td>{helpers.formatCurrency(vdu.currentCost)}</td>
                                    <td>{vdu.caseStatus}</td>
                                    <td>{vdu.binNumber}</td>
                                 </tr>
                              )
                           })}
                        </tbody>
                        <tfoot className="table-footer">
                           <tr>
                              <td colSpan={6} />
                           </tr>
                        </tfoot>
                     </Table>
                  }

                  <Row className="mt-3">
                     <Col>
                        <Button className="shredButton" onClick={handleCloseFinishedGoods}>
                           Close Inventory Count
                        </Button>
                        <span className="ms-3 fw-bold text-danger">
                           Note! No changes are made to GP for finished good inventories all adjustments need to be made inside GP
                        </span>
                     </Col>
                  </Row>
               </CardBody>
            </Card>

            </ServiceThreadSpinner>
      </DesktopContainer>
   );
}